<template>
  <b-modal
      id="restore-avenants-modal"
      ref="modal"
      size="xl"
      centered
      scrollable
      :title="'Récupérer des avenants'"
      @show="loadCandidates"
      @hidden="reset"
  >
    <div v-if="loading" class="text-center py-10">
      <div class="spinner-border text-primary"></div>
      <div class="mt-3">Recherche des avenants candidats...</div>
    </div>
    <div v-else>
      <div v-if="!candidates.length" class="alert alert-info">
        Aucun avenant candidat trouvé.
      </div>
      <div v-else>
        <div class="alert alert-warning py-2 small">
          <i class="fad fa-info-circle me-1"></i>
          Cochez les avenants à rattacher à ce contrat. Ils seront retirés de leur contrat actuel.
        </div>
        <div class="d-flex mb-3">
          <button class="btn btn-light-primary btn-sm me-2" @click="selectAll">Tout cocher</button>
          <button class="btn btn-light btn-sm" @click="clearAll">Tout décocher</button>
          <div class="ms-auto text-muted small">{{ selected.length }} / {{ candidates.length }} sélectionné(s)</div>
        </div>
        <div class="table-responsive">
          <table class="table table-hover align-middle">
            <thead class="bg-light">
            <tr>
              <th style="width:40px"><input type="checkbox" :checked="allSelected" @change="toggleAll"/></th>
              <th>N°</th>
              <th>Police</th>
              <th>Statut</th>
              <th>Type</th>
              <th>Date effet</th>
              <th>Échéance</th>
              <th>Émission</th>
              <th>Contrat actuel</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="a in candidates" :key="a.uuid"
                :class="{'table-primary': isSelected(a.uuid)}">
              <td><input type="checkbox" :value="a.uuid" v-model="selected"/></td>
              <td>{{ a.numero || '—' }}</td>
              <td><code>{{ a.police || '—' }}</code></td>
              <td><span class="badge badge-light">{{ a.status || '—' }}</span></td>
              <td><span class="text-muted small">{{ a.type || '—' }}</span></td>
              <td>{{ formatDate(a.date_effet) }}</td>
              <td>{{ formatDate(a.date_echeance) }}</td>
              <td>{{ formatDate(a.date_emission) }}</td>
              <td>
                <router-link v-if="a.current_contract_uuid" :to="{name:'contract-show', params:{contractUuid: a.current_contract_uuid}}" target="_blank">
                  <code>{{ a.source_police || a.current_contract_uuid }}</code>
                </router-link>
                <span v-else>—</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template #modal-footer="{ cancel }">
      <button class="btn btn-light" @click="cancel">Annuler</button>
      <button class="btn btn-primary"
              :disabled="!canRestore || restoring"
              @click="confirmRestore">
        <span v-if="restoring" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="fad fa-rotate-left me-1"></i>
        Rattacher {{ selected.length }} avenant(s)
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {Component, Prop, Vue} from "vue-property-decorator";
import {api} from "@/services/Api";
import Swal from "sweetalert2";

@Component
export default class RestoreAvenantsModal extends Vue {
  @Prop({required: true}) contractUuid!: string;

  loading = false;
  restoring = false;
  candidates: any[] = [];
  selected: string[] = [];

  get canRestore(): boolean {
    return this.selected.length > 0;
  }

  get allSelected(): boolean {
    return this.candidates.length > 0 && this.selected.length === this.candidates.length;
  }

  isSelected(uuid: string): boolean {
    return this.selected.indexOf(uuid) !== -1;
  }

  selectAll() {
    this.selected = this.candidates.map(c => c.uuid);
  }

  clearAll() {
    this.selected = [];
  }

  toggleAll() {
    if (this.allSelected) this.clearAll(); else this.selectAll();
  }

  formatDate(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR");
  }

  reset() {
    this.candidates = [];
    this.selected = [];
  }

  async loadCandidates() {
    this.reset();
    this.loading = true;
    try {
      const res = await api.get(api.core, "contract/avenants/candidates/" + this.contractUuid);
      if (res?.data?.status === "success") {
        this.candidates = res.data.candidates || [];
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  async confirmRestore() {
    if (!this.canRestore) return;
    const confirm = await Swal.fire({
      title: "Rattacher les avenants ?",
      html: `${this.selected.length} avenant(s) seront déplacés vers ce contrat.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirmer",
      cancelButtonText: "Annuler",
    });
    if (!confirm.isConfirmed) return;
    this.restoring = true;
    let res: any = null;
    let err: any = null;
    try {
      res = await api.post(api.core, "contract/avenants/restore/" + this.contractUuid, {avenantUuids: this.selected});
    } catch (e) {
      err = e;
    }
    this.restoring = false;
    if (err) {
      await Swal.fire({icon: "error", title: "Échec", text: (err as any)?.response?.data?.message || (err as any)?.message || "Erreur"});
      return;
    }
    if (res?.data?.status === "success") {
      await Swal.fire({icon: "success", title: "Fait", html: `${res.data.restored ?? 0} avenant(s) rattaché(s).`});
      (this.$refs.modal as any).hide();
      this.$emit("restored", res.data);
    } else {
      await Swal.fire({icon: "error", title: "Échec", text: res?.data?.message || "Erreur"});
    }
  }
}
</script>
